import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Breadcrumbs = ({ customPath = null }) => {
  const location = useLocation();
  const pathnames = customPath || location.pathname.split('/').filter((x) => x);

  // Define readable names for paths
  const pathNameMap = {
    'place-order': 'Place Order',
    'quote': 'Request a Quote',
    'status': 'Request Status',
    'attorney-service': 'Attorney Services',
    'service-areas': 'Service Areas',
    'blog': 'Blog',
    'cities': 'Cities',
    'palm-desert': 'Palm Desert',
    'cathedral-city': 'Cathedral City',
    'desert-hot-springs': 'Desert Hot Springs',
    'palm-springs': 'Palm Springs',
    'indio': 'Indio',
    'coachella': 'Coachella',
    'la-quinta': 'La Quinta',
    'rancho-mirage': 'Rancho Mirage',
    'indian-wells': 'Indian Wells',
    'thousand-palms': 'Thousand Palms',
    'twentynine-palms': 'Twentynine Palms',
    'yucca-valley': 'Yucca Valley',
    'joshua-tree': 'Joshua Tree',
    'morongo-valley': 'Morongo Valley',
    'landers': 'Landers',
    'riverside': 'Riverside',
    'moreno-valley': 'Moreno Valley',
    'corona': 'Corona',
    'menifee': 'Menifee',
    'temecula': 'Temecula',
    'murrieta': 'Murrieta'
  };

  // Don't show breadcrumbs on homepage
  if (pathnames.length === 0) {
    return null;
  }

  // Get dynamic base URL for deployment flexibility
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://legaldocspecialist.com';
  
  // Generate breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      ...pathnames.map((name, index) => {
        const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
        const position = index + 2;
        const breadcrumbName = pathNameMap[name] || name.charAt(0).toUpperCase() + name.slice(1);
        
        return {
          "@type": "ListItem",
          "position": position,
          "name": breadcrumbName,
          "item": `${baseUrl}${routeTo}`
        };
      })
    ]
  };

  React.useEffect(() => {
    // Add breadcrumb schema to page
    const existingBreadcrumbSchema = document.querySelector('#breadcrumb-schema-data');
    if (existingBreadcrumbSchema) {
      existingBreadcrumbSchema.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'breadcrumb-schema-data';
    script.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);

    return () => {
      const schemaElement = document.querySelector('#breadcrumb-schema-data');
      if (schemaElement) {
        schemaElement.remove();
      }
    };
  }, [location.pathname]);

  return (
    <nav 
      aria-label="Breadcrumb"
      style={{
        backgroundColor: 'var(--bg-card)',
        borderBottom: '1px solid var(--border-light)',
        padding: '1rem 0'
      }}
    >
      <div className="container">
        <ol style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          margin: 0,
          padding: 0,
          listStyle: 'none',
          fontSize: '0.9rem'
        }}>
          <li>
            <Link 
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                color: 'var(--text-secondary)',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
              className="breadcrumb-link"
            >
              <Home size={16} />
              Home
            </Link>
          </li>
          
          {pathnames.map((name, index) => {
            const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
            const isLast = index === pathnames.length - 1;
            const breadcrumbName = pathNameMap[name] || name.charAt(0).toUpperCase() + name.slice(1);

            return (
              <li key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <ChevronRight size={16} color="var(--text-secondary)" />
                {isLast ? (
                  <span style={{
                    color: 'var(--text-primary)',
                    fontWeight: '500'
                  }}>
                    {breadcrumbName}
                  </span>
                ) : (
                  <Link
                    to={routeTo}
                    style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease'
                    }}
                    className="breadcrumb-link"
                  >
                    {breadcrumbName}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      <style>{`
        .breadcrumb-link:hover {
          color: var(--brand-primary) !important;
        }
      `}</style>
    </nav>
  );
};

export default Breadcrumbs;